import { message } from "antd";
import {
  pdf,
  Document,
  Page,
  Text as PDFText,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import { PDFDocument } from "pdf-lib";
import JsBarcode from "jsbarcode";
import { BookingInfo, Product } from "../types/container-unloading";

// PDF Styles - Black and White Warehouse Design
const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontFamily: "Helvetica",
    backgroundColor: "#ffffff",
    flexDirection: "column",
    height: "100%",
  },
  headerSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
    paddingBottom: 5,
    borderBottom: "3px solid #000000",
  },
  logo: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#000000",
  },
  title: {
    fontSize: 28,
    fontWeight: "bold",
    color: "#000000",
    textAlign: "center",
  },
  warehouseNotice: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#000000",
    textAlign: "center",
    marginBottom: 10,
    padding: 4,
    border: "2px solid #000000",
    backgroundColor: "#ffffff",
  },
  bookingTypeIndicator: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#000000",
    textAlign: "center",
    marginBottom: 10,
    padding: 6,
    border: "3px solid #000000",
    backgroundColor: "#ffffff",
  },
  palletId: {
    fontSize: 36,
    fontWeight: "bold",
    color: "#000000",
    textAlign: "center",
    marginBottom: 12,
    padding: 8,
    border: "4px solid #000000",
    backgroundColor: "#ffffff",
  },
  palletIdHighlight: {
    fontSize: 48,
    fontWeight: "bold",
    color: "#000000",
  },
  mainContent: {
    flex: 1,
    justifyContent: "space-between",
  },
  infoGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginBottom: 8,
  },
  infoBox: {
    width: "48%",
    marginBottom: 6,
    marginRight: "2%",
    padding: 6,
    border: "2px solid #000000",
    backgroundColor: "#ffffff",
  },
  label: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#000000",
    marginBottom: 4,
    textTransform: "uppercase",
  },
  value: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#000000",
  },
  largeValue: {
    fontSize: 22,
    fontWeight: "bold",
    color: "#000000",
  },
  productsList: {
    marginBottom: 15,
    padding: 12,
    border: "2px solid #000000",
    backgroundColor: "#ffffff",
  },
  productsTitle: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#000000",
    marginBottom: 6,
  },
  productItem: {
    fontSize: 14,
    color: "#000000",
    marginBottom: 4,
    paddingLeft: 6,
  },
  notesSection: {
    marginBottom: 8,
    padding: 8,
    border: "2px solid #000000",
    backgroundColor: "#ffffff",
  },
  notesTitle: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#000000",
    marginBottom: 6,
  },
  notesText: {
    fontSize: 12,
    color: "#000000",
    lineHeight: 1.3,
  },
  scanSection: {
    textAlign: "center",
    padding: 8,
    border: "2px solid #000000",
    backgroundColor: "#ffffff",
    marginBottom: 8,
  },
  scanText: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#000000",
    fontFamily: "Courier",
  },
  footer: {
    marginTop: 5,
    textAlign: "center",
    fontSize: 10,
    color: "#000000",
    borderTop: "2px solid #000000",
    paddingTop: 4,
  },
  warningBox: {
    padding: 10,
    border: "3px solid #000000",
    backgroundColor: "#ffffff",
    marginBottom: 15,
  },
  warningText: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#000000",
    textAlign: "center",
  },
  barcodesSection: {
    marginTop: 8,
    padding: 6,
    border: "2px solid #000000",
    backgroundColor: "#ffffff",
  },
  barcodesTitle: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#000000",
    marginBottom: 10,
    textAlign: "center",
  },
  barcodeGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
  },
  barcodeItem: {
    width: "48%",
    marginBottom: 4,
    padding: 3,
    border: "1px solid #000000",
    backgroundColor: "#ffffff",
  },
  barcodeImage: {
    width: "100%",
    height: 80,
    marginBottom: 3,
  },
  barcodeText: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#000000",
    textAlign: "center",
    marginBottom: 3,
  },
  productNameText: {
    fontSize: 16,
    color: "#000000",
    textAlign: "center",
  },
  cartonCountText: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#000000",
    textAlign: "center",
    marginTop: 2,
  },
  barcodePageContent: {
    flex: 1,
    padding: 15,
    justifyContent: "flex-start",
  },
});

// Calculate product distribution logic
const calculateProductDistribution = (
  booking: BookingInfo,
  palletNumber: number,
  totalCartons: number,
  cartonsPerPallet: number,
  palletCount: number,
  partialPalletCartons: number
) => {
  const distributedProducts: Array<Product & { cartonsOnPallet: number }> = [];

  for (const product of booking.products) {
    if (!product.actual_cartons) continue;

    const productCartonsOnThisPallet = Math.ceil(
      (product.actual_cartons / totalCartons) *
        (palletNumber === palletCount && partialPalletCartons > 0
          ? partialPalletCartons
          : cartonsPerPallet)
    );

    if (productCartonsOnThisPallet > 0) {
      distributedProducts.push({
        ...product,
        cartonsOnPallet: Math.min(
          productCartonsOnThisPallet,
          product.actual_cartons
        ),
      });
    }
  }

  return distributedProducts;
};

// Create highlighted pallet ID component
const createHighlightedPalletId = (bookingRef: string, suffix: string) => {
  const match = bookingRef.match(/(\d+)/);
  if (match) {
    const numericPart = match[1];
    const beforeNumeric = bookingRef.substring(0, match.index);
    const afterNumeric = bookingRef.substring(match.index! + numericPart.length);
    return (
      <PDFText>
        {beforeNumeric}
        <PDFText style={styles.palletIdHighlight}>{numericPart}</PDFText>
        {afterNumeric}
        {suffix}
      </PDFText>
    );
  }
  return <PDFText>{bookingRef}{suffix}</PDFText>;
};

// Generate barcode base64 for a given EAN
const generateBarcode = (ean: string): string | null => {
  try {
    // Create a canvas element with higher resolution
    const canvas = document.createElement("canvas");
    canvas.width = 600;  // Increased from 300 for higher resolution
    canvas.height = 160; // Increased from 80 for higher resolution

    // Generate barcode using JsBarcode with CODE128 format - improved quality settings
    JsBarcode(canvas, ean, {
      format: "CODE128",
      width: 3,        // Increased from 2 for thicker lines
      height: 100,     // Increased from 50 for taller bars
      displayValue: false, // Don't show text below barcode as we'll show it separately
      margin: 10,      // Increased from 5 for better margins
      background: "#ffffff",
      lineColor: "#000000",
      flat: true,
    });

    // Convert to base64 with high quality
    return canvas.toDataURL("image/png", 1.0);
  } catch (error) {
    console.error("Error generating barcode for EAN:", ean, error);
    return null;
  }
};

// Generate pallet labels
export const generatePalletLabels = async (
  booking: BookingInfo,
  setUpdating: (value: string | null) => void,
  includeBarcodes: boolean = true
) => {
  if (!booking.total_actual_pallets || booking.total_actual_pallets === 0) {
    message.info("No pallets to generate labels for (0 pallets)");
    return;
  }

  try {
    setUpdating(`pallet-labels-${booking.id}`);

    const palletCount = booking.total_actual_pallets;
    const totalCartons = booking.total_actual_cartons || 0;
    const isStorageBooking = !booking.has_delivery_address;

    let pdfBlobs: Blob[] = [];

    if (isStorageBooking) {
      // For storage bookings, generate a single generic label for all pallets
      const palletId = `${booking.booking_reference}-STORAGE`;

      const pages = [
        // Page 1: Main Label Information (always included)
        <Page key="main" size="A4" orientation="landscape" style={styles.page}>
          {/* Header */}
          <View style={styles.headerSection}>
            <PDFText style={styles.logo}>E-FREIGHT</PDFText>
            <PDFText style={styles.title}>WAREHOUSE PALLET LABEL</PDFText>
          </View>

          {/* Booking Type Indicator */}
          <View style={styles.bookingTypeIndicator}>
            <PDFText>STORAGE BOOKING</PDFText>
          </View>

          <View style={styles.mainContent}>
            {/* Pallet ID - prominently displayed */}
            <View style={styles.palletId}>
              {createHighlightedPalletId(booking.booking_reference, '-STORAGE')}
            </View>

            {/* Information Grid */}
            <View style={styles.infoGrid}>
              <View style={styles.infoBox}>
                <PDFText style={styles.label}>Booking Reference</PDFText>
                <PDFText style={styles.largeValue}>
                  {booking.booking_reference}
                </PDFText>
              </View>

              <View style={styles.infoBox}>
                <PDFText style={styles.label}>Company</PDFText>
                <PDFText style={styles.value}>
                  {booking.company_name}
                </PDFText>
              </View>

              <View style={styles.infoBox}>
                <PDFText style={styles.label}>Total Pallets</PDFText>
                <PDFText style={styles.largeValue}>
                  {palletCount}
                </PDFText>
              </View>

              <View style={styles.infoBox}>
                <PDFText style={styles.label}>Total Cartons</PDFText>
                <PDFText style={styles.largeValue}>{totalCartons}</PDFText>
              </View>

              <View style={styles.infoBox}>
                <PDFText style={styles.label}>Generated</PDFText>
                <PDFText style={styles.value}>
                  {new Date().toLocaleDateString()}
                </PDFText>
              </View>
            </View>


            {/* Pallet Note */}
            {booking.internal_pallet_label_note && (
              <View style={styles.notesSection}>
                <PDFText style={styles.notesTitle}>
                  PALLET NOTE:
                </PDFText>
                <PDFText style={styles.notesText}>
                  {booking.internal_pallet_label_note}
                </PDFText>
              </View>
            )}

            {/* Scan Section */}
            <View style={styles.scanSection}>
              <PDFText style={styles.scanText}>{palletId}</PDFText>
            </View>
          </View>

          {/* Footer */}
          <PDFText style={styles.footer}>
            E-Freight Forwarding | Container Unloading System | Generated:{" "}
            {new Date().toLocaleDateString()}
          </PDFText>
        </Page>
      ];

      // Page 2: All Product Barcodes (conditionally included)
      if (includeBarcodes) {
        pages.push(
          <Page key="barcodes" size="A4" orientation="landscape" style={styles.page}>
            <View style={styles.headerSection}>
              <PDFText style={styles.logo}>E-FREIGHT</PDFText>
              <PDFText style={styles.title}>PRODUCT BARCODES</PDFText>
            </View>

            <View style={styles.barcodePageContent}>
              <PDFText style={styles.palletId}>
                {createHighlightedPalletId(booking.booking_reference, '-STORAGE')}
              </PDFText>

              <View style={styles.barcodesSection}>
                <View style={styles.barcodeGrid}>
                  {booking.products
                    .filter((product) => product.ean)
                    .map((product) => {
                      const barcodeBase64 = generateBarcode(product.ean!);
                      return (
                        <View key={product.id} style={styles.barcodeItem}>
                          {barcodeBase64 && (
                            <Image src={barcodeBase64} style={styles.barcodeImage} />
                          )}
                          <PDFText style={styles.barcodeText}>
                            {product.ean}
                          </PDFText>
                          <PDFText style={styles.productNameText}>
                            {product.name}
                          </PDFText>
                          <PDFText style={styles.cartonCountText}>
                            {product.actual_cartons || 0} cartons
                          </PDFText>
                        </View>
                      );
                    })}
                </View>
              </View>
            </View>

            <PDFText style={styles.footer}>
              E-Freight Forwarding | Container Unloading System | Generated:{" "}
              {new Date().toLocaleDateString()}
            </PDFText>
          </Page>
        );
      }

      const PalletLabelDocument = (
        <Document>
          {pages}
        </Document>
      );

      // Generate single PDF blob for storage booking
      const blob = await pdf(PalletLabelDocument).toBlob();
      pdfBlobs = [blob];
    } else {
      // For delivery bookings, generate individual labels for each pallet
      const cartonsPerPallet = Math.floor(totalCartons / palletCount);
      const partialPalletCartons = totalCartons % palletCount;

      for (let palletNumber = 1; palletNumber <= palletCount; palletNumber++) {
      const isPartialPallet =
        palletNumber === palletCount && partialPalletCartons > 0;
      const palletCartons = isPartialPallet
        ? partialPalletCartons
        : cartonsPerPallet;
      const palletId = `${booking.booking_reference}-P${palletNumber
        .toString()
        .padStart(2, "0")}`;

      // Calculate products on this pallet (for barcode display)
      const productsOnPallet = calculateProductDistribution(
        booking,
        palletNumber,
        totalCartons,
        cartonsPerPallet,
        palletCount,
        partialPalletCartons
      );

      const pages = [
        // Page 1: Main Label Information (always included)
        <Page key="main" size="A4" orientation="landscape" style={styles.page}>
            {/* Header */}
            <View style={styles.headerSection}>
              <PDFText style={styles.logo}>E-FREIGHT</PDFText>
              <PDFText style={styles.title}>WAREHOUSE PALLET LABEL</PDFText>
            </View>

            {/* Booking Type Indicator */}
            <View style={styles.bookingTypeIndicator}>
              <PDFText>DELIVERY BOOKING</PDFText>
            </View>

            <View style={styles.mainContent}>
              {/* Pallet ID - prominently displayed */}
              <View style={styles.palletId}>
                {createHighlightedPalletId(booking.booking_reference, `-P${palletNumber.toString().padStart(2, '0')}`)}
              </View>

              {/* Warning for partial pallet */}
              {isPartialPallet && (
                <View style={styles.warningBox}>
                  <PDFText style={styles.warningText}>
                    PARTIAL PALLET - HANDLE WITH CARE
                  </PDFText>
                </View>
              )}

              {/* Information Grid */}
              <View style={styles.infoGrid}>
                <View style={styles.infoBox}>
                  <PDFText style={styles.label}>Booking Reference</PDFText>
                  <PDFText style={styles.largeValue}>
                    {booking.booking_reference}
                  </PDFText>
                </View>

                <View style={styles.infoBox}>
                  <PDFText style={styles.label}>Company</PDFText>
                  <PDFText style={styles.value}>
                    {booking.company_name}
                  </PDFText>
                </View>

                <View style={styles.infoBox}>
                  <PDFText style={styles.label}>Pallet Number</PDFText>
                  <PDFText style={styles.largeValue}>
                    {palletNumber} of {palletCount}
                  </PDFText>
                </View>

                <View style={styles.infoBox}>
                  <PDFText style={styles.label}>Cartons on This Pallet</PDFText>
                  <PDFText style={styles.largeValue}>{palletCartons}</PDFText>
                </View>

                <View style={styles.infoBox}>
                  <PDFText style={styles.label}>
                    Total Cartons Across All Pallets
                  </PDFText>
                  <PDFText style={styles.value}>{totalCartons}</PDFText>
                </View>

                <View style={styles.infoBox}>
                  <PDFText style={styles.label}>Generated</PDFText>
                  <PDFText style={styles.value}>
                    {new Date().toLocaleDateString()}
                  </PDFText>
                </View>
              </View>


              {/* Pallet Note */}
              {booking.internal_pallet_label_note && (
                <View style={styles.notesSection}>
                  <PDFText style={styles.notesTitle}>
                    PALLET NOTE:
                  </PDFText>
                  <PDFText style={styles.notesText}>
                    {booking.internal_pallet_label_note}
                  </PDFText>
                </View>
              )}


              {/* Scan Section */}
              <View style={styles.scanSection}>
                <PDFText style={styles.scanText}>{palletId}</PDFText>
              </View>
            </View>

            {/* Footer */}
            <PDFText style={styles.footer}>
              E-Freight Forwarding | Container Unloading System | Generated:{" "}
              {new Date().toLocaleDateString()}
            </PDFText>
          </Page>
      ];

      // Page 2: Product Barcodes (conditionally included)
      if (includeBarcodes) {
        pages.push(
          <Page key="barcodes" size="A4" orientation="landscape" style={styles.page}>
            <View style={styles.headerSection}>
              <PDFText style={styles.logo}>E-FREIGHT</PDFText>
              <PDFText style={styles.title}>PRODUCT BARCODES</PDFText>
            </View>

            <View style={styles.barcodePageContent}>
              <PDFText style={styles.palletId}>
                {createHighlightedPalletId(booking.booking_reference, `-P${palletNumber.toString().padStart(2, '0')}`)}
              </PDFText>

              <View style={styles.barcodesSection}>
                <View style={styles.barcodeGrid}>
                  {productsOnPallet
                    .filter((product) => product.ean)
                    .map((product) => {
                      const barcodeBase64 = generateBarcode(product.ean!);
                      return (
                        <View key={product.id} style={styles.barcodeItem}>
                          {barcodeBase64 && (
                            <Image src={barcodeBase64} style={styles.barcodeImage} />
                          )}
                          <PDFText style={styles.barcodeText}>
                            {product.ean}
                          </PDFText>
                          <PDFText style={styles.productNameText}>
                            {product.name}
                          </PDFText>
                          <PDFText style={styles.cartonCountText}>
                            {product.cartonsOnPallet} cartons
                          </PDFText>
                        </View>
                      );
                    })}
                </View>
              </View>
            </View>

            <PDFText style={styles.footer}>
              E-Freight Forwarding | Container Unloading System | Generated:{" "}
              {new Date().toLocaleDateString()}
            </PDFText>
          </Page>
        );
      }

      const PalletLabelDocument = (
        <Document>
          {pages}
        </Document>
      );

      // Generate PDF blob for this pallet
      const blob = await pdf(PalletLabelDocument).toBlob();
      pdfBlobs.push(blob);
    }

    // Handle download based on booking type and pallet count
    if (isStorageBooking) {
      // Storage bookings always have 1 label regardless of pallet count
      const url = URL.createObjectURL(pdfBlobs[0]);
      const link = document.createElement("a");
      const palletId = `${booking.booking_reference}-STORAGE`;
      link.href = url;
      link.download = `${palletId.replace("#", "_")}_Label.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else {
      // Delivery bookings - always create multi-page PDFs (2 pages per pallet)
      const mergedPdf = await PDFDocument.create();

      for (const blob of pdfBlobs) {
        const arrayBuffer = await blob.arrayBuffer();
        const pdf = await PDFDocument.load(arrayBuffer);
        // Copy all pages from each PDF (each pallet PDF now has 2 pages)
        const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
        copiedPages.forEach((page) => mergedPdf.addPage(page));
      }

      const mergedPdfBytes = await mergedPdf.save();
      const mergedBlob = new Blob([mergedPdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(mergedBlob);
      const link = document.createElement("a");

      if (palletCount === 1) {
        const palletId = `${booking.booking_reference}-P01`;
        link.href = url;
        link.download = `${palletId.replace("#", "_")}_Label.pdf`;
      } else {
        link.href = url;
        link.download = `${booking.booking_reference.replace("#", "_")}_All_Pallet_Labels.pdf`;
      }

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
    }

    message.success(`Generated pallet label(s) successfully!`);
  } catch (error) {
    console.error("Error generating pallet labels:", error);
    message.error("Failed to generate pallet labels");
  } finally {
    setUpdating(null);
  }
};
